<template>
  <div class="smart-select" ref="smart_select">
    <div class="smart-select__header"
         :class="{'smart-select__header-border': !borderLess, 'smart-select__header_disabled': disabled}"
         @click="open()">
      <input type="text" class="smart-select__input"
             ref="input"
             :style="inputStyles"
             :placeholder="placeholder"
             @blur="onBlur"
             v-model="text"
             :class="{'smart-select__input_error': isNotValid}"
             @change="onChange"
             @keyup.enter="onEnter">
      <slot></slot>
    </div>
    <template v-if="isOpened && filteredOptions.length">
      <div class="smart-select__overlay"
           @click.prevent.stop="close()"/>
      <ul class="smart-select__list"
          v-if="filterOptions && filteredOptions.length"
           :class="'smart-select__list_' + listPosition"
           :style="listStyle">
        <li class="smart-select__item"
             v-for="(option, index) in filteredOptions"
             :key="index"
             :class="{'smart-select__item_selected': option.value === getValue()}"
             @click.stop="select(option)" v-html="listLabel(option)">

        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'

const MIN_CHARS_NUMBER = 1
/**
 * is used in Advanced Options
 * parent component for BaseSmartInputAutocomplete.vue BaseSmartInputSearch.vue
 */
export default {
  name: 'BaseSmartInput',
  data () {
    return {
      isOpened: false,
      listStyle: {},
      listPosition: '',
      /* text entered by user in input field */
      text: '',
      filteredOptions: [],
      /**
       * we use debounced function to avoid call {@see #_calculateFilteredOptions} too frequently
       * @see https://lodash.com/docs/4.17.15#debounce
       * */
      debouncedFilterFunc: null,
      /** selected by user option. Has next format:
       * { label: String,
       *   value: String }
       * */
      selectedOption: {}
    }
  },
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  created () {
    this.filteredOptions = _.cloneDeep(this.options)
    this.selectedOption = this.getSelectedOptions()
    this.text = this.selectedOption ? this.selectedOption.label : ''
    window.addEventListener('resize', _.debounce(() => {
      this.setListStyle()
    }, 50), false)
    if (this.easyModel) {
      this.text = this.modelValue
      this.$watch('text', (v) => {
        this.$emit('change', v)
      })
    }
    if (this.easyValueModel) {
      this.$watch('modelValue', (v) => {
        this.text = (this.options.find(i => i.value === v) || {}).label || ''
      })
      this.text = (this.options.find(i => i.value === this.modelValue) || {}).label || ''
    }
  },
  props: {
    /*
      * option format: {
      *   label: String,
      *   value: String
      * }
      * */
    disabled: Boolean,
    options: {
      type: Array,
      default: () => []
    },
    inputStyles: {
      type: Object,
      default: () => ({})
    },
    easyModel: {
      type: Boolean,
      default: false
    },
    easyValueModel: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    /**
     * is used to:
     * 1. notify external observer about new selected value
     * 2. set into the component earlier selected value
     * */
    modelValue: {
      default: null
    },
    borderLess: {
      type: Boolean,
      default: false
    },
    /* external validation */
    isNotValid: {
      type: Boolean,
      default: false
    },
    allowFreeText: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    text: 'textHandler',
    options: 'optionsHandler'
  },
  methods: {
    getSelectedOptions () {
      return _.cloneDeep(this.options.find(option => option.value === this.getValue())) || {}
    },
    /**
     * invokes when options is changed outside the component
     * */
    optionsHandler () {
      this.filteredOptions = _.cloneDeep(this.options)
      this.selectedOption = {}
      this.text = ''
    },
    /**
     * invokes when text changes by user
     * */
    textHandler () {
      if (this.isOpened) {
        this.$emit('input-text', this.text)
        this.filterOptions()
      }
    },
    open () {
      this.$emit('state', 'open')
      this.isOpened = true
      this.text = null
      this.setListStyle()
    },
    onBlur () {
      this.onChange()
      this.$emit('blur', this.text)
    },
    onChange () {
      if (this.allowFreeText) {
        this.selectedOption = { label: this.text, value: this.text }
        this.onEnter()
      }
    },
    onEnter () {
      this.changeModel()
    },
    clear () {
      this.text = ''
      this.selectedOption = {}
      if (this.easyModel) this.$emit('change', '')
    },
    /**
     * method invokes debounced function by changing text by user
     * */
    filterOptions () {
      if (!this.debouncedFilterFunc) {
        this.debouncedFilterFunc = _.debounce(this.calculateFilteredOptions, 100)
      } else {
        this.debouncedFilterFunc.cancel()
      }
      this.debouncedFilterFunc()
    },
    /**
     * @alias _calculateFilteredOptions
     * method stylizes dropdown list based on input text
     * */
    calculateFilteredOptions () {
      if (!this.text || this.text.length < MIN_CHARS_NUMBER) {
        this.filteredOptions = this.options
      } else {
        this.filteredOptions = _.cloneDeep(this.options).filter(item => {
          const label = String(item.label)
          // code styling list item label
          if (label.toLowerCase().includes(this.text.toLowerCase())) {
            const split = label.split(this.text)
            item.stylizedLabel = split[0]
            for (let i = 1; i < split.length; i++) {
              item.stylizedLabel += `<span class="smart-select__item_entered">${this.text}</span>${split[ i ]}`
            }
            return true
          }
          return false
        })
      }
    },
    close () {
      this.$emit('state', 'close')
      this.$refs.input.blur()
      this.$set(this, 'isOpened', false)
      this.changeModel()
    },
    /**
     * method is invoked to notify external observer about changing model
     */
    changeModel () {
      this.text = this.selectedOption ? this.selectedOption.label : ''
      const value = this.selectedOption ? this.selectedOption.value : ''
      if (value || this.easyModel || this.easyValueModel) {
        this.$emit(
          'change',
          this.easyModel
            ? this.text || ''
            : this.easyValueModel ? value : { value, text: this.text })
      }
      this.$emit('apply')
    },
    listLabel (option) {
      if (option.stylizedLabel) {
        return option.stylizedLabel
      }
      return option.label
    },
    select (option) {
      this.selectedOption = _.cloneDeep(option)
      this.$emit('input-text', this.text)
      this.close()
    },
    /**
     * method creates stylized dropdown list
     */
    setListStyle () {
      const ref = this.$refs[ 'smart_select' ]
      if (!ref) {
        return
      }
      const style = {}
      const { top, bottom, left, right } = ref.getBoundingClientRect()
      const topShift = top
      const bottomShift = window.innerHeight - bottom
      let verticalProp, maxHeight
      if (topShift < bottomShift) {
        verticalProp = 'top'
        maxHeight = Math.round(bottomShift) - 30
      } else {
        verticalProp = 'bottom'
        maxHeight = Math.round(top) - 30
      }
      this.listPosition = verticalProp
      style[ verticalProp ] = '100%'
      style[ 'max-height' ] = `${maxHeight}px`
      const leftShift = left
      const rightShift = window.innerWidth - right
      const horizontalProp = rightShift < leftShift ? 'right' : 'left'
      style[ horizontalProp ] = 0
      this.listStyle = style
    },
    getValue () {
      return _.get(this.modelValue, 'value')
    },
    focus() {
      if (!this.$refs.input) {
        return
      }
      this.$refs.input.focus()
    },
    blur() {
      if (!this.$refs.input) {
        return
      }
      this.$refs.input.blur()
    }
  }
}
</script>
<!--do not add "scoped" here!-->
<style lang="scss">
  .smart-select {
    position: relative;
    user-select: none;
    cursor: default;
    height: 60px !important;
    &__header {
      display: flex;
      height: 100%;
      background-color: #fff;
      box-sizing: border-box;
      &-border {
        border: 2px solid $rfx-light-steel;
        border-radius: 4px;
      }

      &_disabled {
        pointer-events: none;
      }
    }
    &__input {
      display: flex;
      align-items: center;
      height: 100%;
      flex: 1 1 100%;
      font-size: 14px;
      padding-left: 20px;
      box-sizing: border-box;
      outline: none;
      border: none;
      color: $rfx-dark-march;
      font-weight: 500;
      @include ellipsis;

      &::placeholder {
        color: $rfx-silver;
        font-size: 14px;
      }

      &_error {
        &::placeholder {
          color: $rfx-light-red;
          font-size: 14px;
        }
      }
    }

    &__overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9998;
    }
    &__list {
      position: absolute;
      z-index: 9999;
      min-width: 100%;
      max-width: 600px;
      margin: 1px 0;
      padding: 0;
      background: #fff;
      list-style-type: none;
      overflow-x: hidden;
      overflow-y: auto;
      outline: 1px solid #f0f0f0;

      &_top {
        box-shadow: 0 15px 40px -20px rgba(0, 0, 0, .3);
      }

      &_bottom {
        box-shadow: 0 -15px 40px -20px rgba(0, 0, 0, .3);
      }
    }
    &__item {
      height: 30px;
      line-height: 30px;
      padding: 0 20px;
      @include ellipsis;
      font-size: 12px;
      color: #000;

      &_selected,
      &:hover {
        color: #fff !important;
        background-color: $rfx-bright-blue;
      }

      &_selected * {
        color: #fff !important;
      }

      &:hover * {
        color: #fff !important;
      }

      &_entered {
        color: $rfx-light-blue;
      }
    }
  }

</style>
